import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnInit, Signal, ViewEncapsulation } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent } from 'ui-library';

@Component({
  selector: 'app-origin-dialog',
  templateUrl: './origin-dialog.component.html',
  styleUrls: ['./origin-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ButtonComponent,
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    DragDropModule,
    TranslateModule
  ]
})

export class OriginDialogComponent implements OnInit {
  // Services
  public readonly dialogRef: MatDialogRef<OriginDialogComponent> = inject(MatDialogRef<OriginDialogComponent>);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly pop: Pop = inject(Pop);
  public readonly data: any = inject(MAT_DIALOG_DATA);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly tools: Tools = inject(Tools);

  // Components API

  // Signals
  public readonly isPrivileged: Signal<boolean> = this.authService.isPrivileged

  // Variables
  onAdd = new EventEmitter();
  modelData: any;
  originForm: any;
  mapCoord: any = {
    north: { type: Number },
    east: { type: Number },
    south: { type: Number },
    west: { type: Number }
  };
  newObject: any = {};


  constructor() { }

  ngOnInit(): void {
    this.modelData = this.data.modelData;
    this.initializeForm()
  }

  initializeForm() {
    if (!this.modelData.mapImage) {
      const centerLat = this.modelData.mapCenter?.lat || 0;
      const centerLon = this.modelData.mapCenter?.lon || 0;
      this.modelData.mapCoord = {
        north: centerLat + 0.0002,
        south: centerLat - 0.0002,
        west: centerLon - 0.0002,
        east: centerLon + 0.0002
      };
    }

    this.originForm = this.fb.group({
      north: [this.modelData.mapCoord.north, Validators.required],
      south: [this.modelData.mapCoord.south, Validators.required],
      west: [this.modelData.mapCoord.west, Validators.required],
      east: [this.modelData.mapCoord.east, Validators.required],
      latitude: [this.modelData.origin.position.lat, Validators.required],
      longitude: [this.modelData.origin.position.lon, Validators.required],
      mapLatitude: [this.modelData.mapCenter?.lat, Validators.required],
      mapLongitude: [this.modelData.mapCenter?.lon, Validators.required],
      angle: [this.modelData.origin.angle, Validators.required],
      mapAngle: [this.modelData.origin.mapAngle, Validators.required],
      zoom: [this.modelData.origin.zoom, Validators.required],
      zoomEnabled: [this.modelData.origin.zoomEnabled, Validators.required],
      panEnabled: [this.modelData.origin.panEnabled, Validators.required]
    });

    this.newObject = this.originForm.value;
    this.newObject.mapImage = this.modelData.mapImage;

    // clean dirty url value :[]
    if (this.modelData.mapImageSrc) {
      const srcArray = this.modelData.mapImageSrc.split('https')

      if (srcArray.length > 2) {
        const lastUrl = 'https' + srcArray[srcArray.length - 1]
        this.newObject.mapImageSrc = lastUrl;
        this.modelData.mapImageSrc = lastUrl;
      } else {
        this.newObject.mapImageSrc = this.modelData.mapImageSrc;
      }
    }
  }



  formData(ev: any) {
    this.newObject[ev.target.id] = ev.target.value;
    const cte = 0.001;
    const cte2 = 0.002;

    if (ev.target.id == 'north' || ev.target.id == 'south' || ev.target.id == 'latitude') this.newObject[ev.target.id] = this.setLimitValue(this.newObject[ev.target.id], 90, -90)
    else this.newObject[ev.target.id] = this.setLimitValue(this.newObject[ev.target.id], 180, -180);

    if (this.mapCoord.north <= this.mapCoord.south) {
      this.mapCoord.south = this.mapCoord.north - cte2;
    }

    if (this.newObject.east <= this.newObject.west) {
      this.newObject.west = this.newObject.east - cte2;
    }

    this.updateOrigin();
  }


  updateOrigin() {
    delete this.modelData.resizedImage;

    let objectToSend = {
      ...this.modelData,
      id: this.data.projectId,
      mapImage: this.newObject.mapImage,
      mapImageSrc: this.newObject.mapImageSrc,
      mapCenter: {
        lat: parseFloat(this.newObject.mapLatitude),
        lon: parseFloat(this.newObject.mapLongitude)
      },
      mapCoord: {
        north: parseFloat(this.newObject.north),
        south: parseFloat(this.newObject.south),
        east: parseFloat(this.newObject.east),
        west: parseFloat(this.newObject.west),
      },
      origin: this.modelData.origin,
    };

    objectToSend.origin.position.lat = parseFloat(this.newObject.latitude);
    objectToSend.origin.position.lon = parseFloat(this.newObject.longitude);
    objectToSend.origin.angle = parseFloat(this.newObject.angle);
    objectToSend.origin.mapAngle = 0;
    objectToSend.origin.zoom = parseFloat(this.newObject.zoom);
    objectToSend.origin.zoomEnabled = this.newObject.zoomEnabled;
    objectToSend.origin.panEnabled = this.newObject.panEnabled;

    this.onAdd.emit({ currentModel: objectToSend });
  }

  setLimitValue(point: number, val: number, _val: number) {
    if ((point > val)) {
      point = val - 0.002;
    } else if (point < _val) {
      point = _val + 0.002;
    }
    return point;
  }

  checkBoxEvents(ev: any) {
    this.newObject[ev.source.id] = ev.checked;
    this.formData({ target: { id: ev.source.id, value: ev.checked } })
  }

  dismiss(): void {
    this.data.dismissed = true
    this.dialogRef.close(this.data);
  }

  centerHere(): void {
    if (!this.data.googleMap) {
      this.pop.toast(this.translate.instant('viewer.error.no_map'), 'danger', 4000);
      return;
    }

    const center = this.data.googleMap.getCenter();
    const lat = center.lat();
    const lng = center.lng();
    console.log('----> lat :>>', lat);
    console.log('----> lng :>>', lng);

    this.originForm.patchValue({
      mapLatitude: lat,
      mapLongitude: lng
    });

    this.newObject.mapLatitude = lat;
    this.newObject.mapLongitude = lng;

    this.updateOrigin();
  }

  resetImageFromSource(): void {
    if (!this.modelData.mapImageSrc) return;

    this.newObject.mapImage = this.modelData.mapImageSrc;
    this.modelData.mapImage = this.modelData.mapImageSrc;

    this.updateOrigin();
  }

  close() {
    this.dialogRef.close({})
  }
}
